import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from "react";
import AddBusinessIcon from '@mui/icons-material/AddBusiness';
import { useNotification } from '../../hooks/useNotification';
import { useErrorInputs } from "../../hooks/useErrorInputs";
import { useDataAuth } from "../../hooks/useDataAuth";
import FormHelperText from "@mui/material/FormHelperText";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from '@mui/material/Tooltip';
import Select from "@mui/material/Select";
import Grid from "@mui/material/Grid";

import { getCountries, getStatesAndCities } from "../../services/sharedControls.service";
import { fetchServiceJson } from "../../services/common.services";
import { fetCompanies } from '../../services/contact.service';
import { CreateClient } from './dialog/CreateClient';
import { MultipleSelectAutoComplete } from '../others/MultipleSelectAutoComplete';
import '../../styles/components/_forms.scss';
import { useTranslation } from "react-i18next";
export const CreateFormContact = forwardRef(({ dto, handleInputChange, handleDirectInputChange, isEdit, isView }, ref) => {
    const { t } = useTranslation();
    const [errorInputs, handleErrorInput] = useErrorInputs({});
    const [sendNotification] = useNotification();
    const [{ userSession }] = useDataAuth();

    const [countries, setCountries] = useState([]);
    const [states, setStates] = useState([]);
    const [cities, setCities] = useState([]);
    const [roles, setRoles] = useState([]);
    const [origin, setOrigin] = useState([]);
    const [relationshipStatus, setRelationshipStatus] = useState([]);
    const [openDialogAddClient, setOpenDialogAddClient] = useState(false);
    const [companies, setCompanies] = useState([]);
    const fetchData = useCallback(async () => {
        const response = await getCountries(userSession.token);
        if (response.data)
            setCountries(response.data);
    }, [userSession.token]);

    const GetStatesAndCities = useCallback(async (action, variables) => {
        const response = await getStatesAndCities(userSession.token, action, variables);
        if (response.data) {
            if (action === 'STATE')
                setStates(response.data);
            else {
                setCities(response.data);
            }
        }
    }, [userSession.token]);

    useEffect(() => {
        fetchData();
        GetStatesAndCities('STATE', { id_country: 1 });
    }, [fetchData, GetStatesAndCities]);

    useEffect(() => {
        GetStatesAndCities('CITY', { code: dto.state });
    }, [dto.state, GetStatesAndCities]);

    const handleChangeCountryState = (e, action) => {
        const { name, value } = e.target;
        handleDirectInputChange(name, value);
        if (action === 'STATE') {
            if (value === 'CO') {
                GetStatesAndCities('STATE', { id_country: 1 });
            }
        } else if (action === 'CITY') {
            GetStatesAndCities('CITY', { code: value });
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await fetchServiceJson('MENU-CONTACT', userSession.token);
                if (response.data && response.data.length > 0) {
                    const labels = response.data;
                    labels.forEach((item) => {
                        const parsedValue = JSON.parse(item.value);
                        if (item.field === "rol") {
                            setRoles(parsedValue);
                        }
                        else if (item.field === "position") {
                            setRelationshipStatus(parsedValue);
                        }
                        else if (item.field === "origin") {
                            setOrigin(parsedValue);
                        }
                    });
                }
            } catch (e) {
                console.error("Error fetching contact JSON:", e.message);
            }
        };
        fetchData();
    }, [userSession.token]);

    useEffect(() => {
        const fetchData = async () => {
            const response = await fetCompanies(userSession.token);
            if (response.data)
                setCompanies(response.data);
        };
        fetchData();
    }, [userSession.token]);

    const validateField = (name, value) => {
        if (value === null || value === undefined || (typeof value === 'string' && value.trim() === '') || (Array.isArray(value) && value.length === 0)) {
            handleErrorInput(name, { isError: true, messageError: t("validation.requiredField") });
            return false;
        } else {
            handleErrorInput(name, { isError: false, messageError: '' });
            return true;
        }
    };

    useImperativeHandle(ref, () => ({
        validateForm,
    }));

    const validateForm = () => {
        let hasError = false;
        const requiredFields = ["name", "origin", "email"];
        requiredFields.forEach((field) => {
            const isValid = validateField(field, dto[field]);
            if (!isValid) hasError = true;
        });
        if (dto.clients.length === 0) {
            handleErrorInput("clients", { isError: true, messageError: t("validation.requiredField") });
            hasError = true;
        }
        else {
            handleErrorInput("clients", { isError: false, messageError: '' });
        }
        return !hasError;
    };

    const handleopenDialogAddClient = (saved) => {
        if (saved && saved.id_client) {
            try {
                const fetchData = async () => {
                    const response = await fetCompanies(userSession.token);
                    if (response && response.data) {
                        setCompanies(response.data);
                        handleDirectInputChange('clients', [...dto.clients, saved.id_client.toString()]);
                        sendNotification({ variant: 'success', msg: t("messages.saveSuccess") });
                    }
                };
                fetchData();
            } catch (error) {
                sendNotification({ variant: 'error', msg: t("messages.saveError") });
            }
        }
        setOpenDialogAddClient(!openDialogAddClient);
    }
    const handleData = (e) => {
        const { name, value } = e.target;
        if (name === 'extension' && value.length > 5) {
            return;
        }
        handleDirectInputChange(name, value.toString());
    }

    return (
        <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 8 }}>
                <TextField
                    fullWidth
                    label="Nombre Completo"
                    size="small"
                    name="name"
                    value={dto.name}
                    onChange={handleInputChange}
                    error={Boolean(errorInputs.name?.isError)}
                    helperText={errorInputs.name?.messageError || ''}
                    required
                />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
                <FormControl
                    fullWidth
                    size="small"
                    error={Boolean(errorInputs.origin?.isError)}
                    required
                >
                    <InputLabel id="origin-label">Origen</InputLabel>
                    <Select
                        labelId="origin-label"
                        name="origin"
                        value={dto.origin}
                        onChange={handleInputChange}
                    >
                        {origin.map((origin) => (
                            <MenuItem key={origin.code} value={origin.name}>
                                {origin.name}
                            </MenuItem>
                        ))}
                    </Select>
                    {errorInputs.origin?.isError && (
                        <FormHelperText>{errorInputs.origin.messageError}</FormHelperText>
                    )}
                </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                    fullWidth
                    label="Celular"
                    size="small"
                    name="cellphone"
                    type="text"
                    value={dto.cellphone}
                    onChange={(e) => {
                        const newValue = e.target.value.replace(/\D/g, '').slice(0, 10);
                        handleInputChange({ target: { name: 'cellphone', value: newValue } });
                    }}
                    inputProps={{
                        maxLength: 10,
                        inputMode: 'numeric',
                        pattern: '[0-9]*',
                    }}
                />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
                <TextField
                    fullWidth
                    label="Teléfono"
                    size="small"
                    name="phone"
                    type="text"
                    value={dto.phone}
                    onChange={(e) => {
                        const newValue = e.target.value.replace(/\D/g, '').slice(0, 10);
                        handleInputChange({ target: { name: 'phone', value: newValue } });
                    }}
                    error={Boolean(errorInputs.phone?.isError)}
                    helperText={errorInputs.phone?.messageError || ''}
                    className='input-no-spinner'
                />
            </Grid>
            <Grid size={{ xs: 12, md: 1 }}>
                <TextField
                    fullWidth
                    label="Extensión"
                    size="small"
                    name="extension"
                    type="text"
                    value={dto.extension}
                    onChange={handleData}
                    error={Boolean(errorInputs.phone?.isError)}
                    helperText={errorInputs.phone?.messageError || ''}

                />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                    fullWidth
                    label="Email"
                    size="small"
                    name="email"
                    value={dto.email}
                    onChange={handleInputChange}
                    error={Boolean(errorInputs.email?.isError)}
                    helperText={errorInputs.email?.messageError || ''}
                    required
                />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
                <FormControl
                    fullWidth
                    size="small"
                    error={Boolean(errorInputs.country?.isError)}
                >
                    <InputLabel id="country-label">País</InputLabel>
                    <Select
                        labelId="country-label"
                        name="country"
                        value={dto.country}
                        onChange={(e) => handleChangeCountryState(e, 'STATE')}
                    >
                        {countries.map((country) => (
                            <MenuItem key={country.code} value={country.code}>
                                {country.name}
                            </MenuItem>
                        ))}
                    </Select>
                    {errorInputs.country?.isError && (
                        <FormHelperText>{errorInputs.country.messageError}</FormHelperText>
                    )}
                </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
                {dto.country === 'CO' ?
                    <FormControl
                        fullWidth size="small"
                        error={Boolean(errorInputs.state?.isError)}
                    >
                        <InputLabel id="state-label">Departamento</InputLabel>
                        <Select
                            labelId="state-label"
                            name="state"
                            value={dto.state}
                            onChange={(e) => handleChangeCountryState(e, 'CITY')}
                        >
                            {states.map((option) => (
                                <MenuItem key={option.id_state} value={option.code}>
                                    {option.name}
                                </MenuItem>
                            ))}
                        </Select>
                        {errorInputs.state?.isError && (
                            <FormHelperText>{errorInputs.state.messageError}</FormHelperText>
                        )}
                    </FormControl>
                    :
                    <TextField
                        label="Estado"
                        name="state"
                        value={dto.state}
                        onChange={handleInputChange}
                        fullWidth
                        size="small"
                        error={Boolean(errorInputs.state?.isError)}
                        helperText={errorInputs.state?.messageError || ''}
                    />
                }
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
                {dto.country === 'CO' ?
                    <FormControl
                        fullWidth size="small"
                        error={Boolean(errorInputs.city?.isError)}
                    >
                        <InputLabel id="city-label">Ciudad</InputLabel>
                        <Select
                            labelId="city-label"
                            name="city"
                            value={dto.city}
                            onChange={handleInputChange}
                        >
                            {cities.map((option) => (
                                <MenuItem key={option.code} value={option.code}>
                                    {option.name}
                                </MenuItem>
                            ))}
                        </Select>
                        {errorInputs.city?.isError && (
                            <FormHelperText>{errorInputs.city.messageError}</FormHelperText>
                        )}
                    </FormControl>
                    :
                    <TextField
                        label="Ciudad/Localidad"
                        name="city"
                        value={dto.city}
                        onChange={handleInputChange}
                        fullWidth
                        size="small"
                        error={Boolean(errorInputs.city?.isError)}
                        helperText={errorInputs.city?.messageError || ''}
                    />
                }
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                    fullWidth
                    label="Dirección"
                    size="small"
                    name="address"
                    value={dto.address}
                    onChange={handleInputChange}
                    error={Boolean(errorInputs.address?.isError)}
                    helperText={errorInputs.address?.messageError || ''}
                />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                    fullWidth
                    label="Cargo"
                    size="small"
                    name="title"
                    value={dto.title}
                    onChange={handleInputChange}
                    error={Boolean(errorInputs.title?.isError)}
                    helperText={errorInputs.title?.messageError || ''}
                />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                    fullWidth
                    label="Área"
                    size="small"
                    name="area"
                    value={dto.area}
                    onChange={handleInputChange}
                    error={Boolean(errorInputs.area?.isError)}
                    helperText={errorInputs.area?.messageError || ''}
                />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
                <FormControl
                    fullWidth
                    size="small"
                    error={Boolean(errorInputs.role?.isError)}
                >
                    <InputLabel id="role-label">Rol</InputLabel>
                    <Select
                        labelId="role-label"
                        name="role"
                        value={dto.role}
                        onChange={handleInputChange}
                    >
                        {roles.map((role) => (
                            <MenuItem key={role.code} value={role.name}>
                                {role.name}
                            </MenuItem>
                        ))}
                    </Select>
                    {errorInputs.role?.isError && (
                        <FormHelperText>{errorInputs.role.messageError}</FormHelperText>
                    )}
                </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
                <FormControl
                    fullWidth
                    size="small"
                    error={Boolean(errorInputs.relationship_status?.isError)}
                >
                    <InputLabel id="relationship_status-label">Estado de Relación</InputLabel>
                    <Select
                        labelId="relationship_status-label"
                        name="relationship_status"
                        value={dto.relationship_status}
                        onChange={handleInputChange}
                    >
                        {relationshipStatus.map((status) => (
                            <MenuItem key={status.code} value={status.name}>
                                {status.name}
                            </MenuItem>
                        ))}
                    </Select>
                    {errorInputs.relationship_status?.isError && (
                        <FormHelperText>{errorInputs.relationship_status.messageError}</FormHelperText>
                    )}
                </FormControl>
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
                <Grid container spacing={1} alignItems="center">
                    <Grid size={{ xs: 10 }}>
                        <MultipleSelectAutoComplete
                            label={'Empresas'}
                            data={companies}
                            values={dto.clients}
                            handleChange={({ target }) => handleDirectInputChange('clients', target.value)}
                            name={'clients'}
                            error={errorInputs.clients?.isError}
                        />
                    </Grid>
                    <Grid size={{ xs: 12 }} sx={{ display: 'flex', justifyContent: 'flex-end', padding: 0 }}>
                        <Tooltip title={t("add")} placement="top" arrow>
                            <IconButton
                                aria-label="add"
                                onClick={handleopenDialogAddClient}
                                sx={{ color: '#6728AF' }}
                                disabled={isView}

                            >
                                <AddBusinessIcon color="primary" />
                            </IconButton>
                        </Tooltip>
                    </Grid>
                </Grid>
            </Grid>
            <CreateClient data={dto} open={openDialogAddClient} onClose={handleopenDialogAddClient} />
        </Grid>
    );
});